/**
 * Kategoriewechsel-Knopf der Aktionsspalte (D-16/D-17/D-18/D-19, Phase 34): ersetzt das
 * Kategoriefeld je Zeile aus der flachen Sketch-Fassung — die Gruppenzeile nennt die Kategorie
 * bereits, ein zusätzliches Auswahlfeld in jeder Zeile wäre redundant und kostet eine Spalte in
 * einer Tabelle mit zehn.
 *
 * Gebaut als Mantine `Menu`-Compound, bewusst NICHT über die allgemeinere Overlay-Grundkomponente
 * ohne eingebaute Menüsemantik: `Menu` bringt Tastaturnavigation, Fokusführung und deaktivierbare
 * Einträge von Haus aus mit. Mit der Grundkomponente müsste all das von Hand nachgebaut werden —
 * und der Kategoriewechsel muss ausdrücklich mit der Tastatur bedienbar sein (D-19: bewusst KEIN
 * Drag-and-drop zwischen den Gruppen, wegen Tastatur und Touch). Keine zusätzliche Mikrobewegung:
 * die Standardanimation des Menüs bleibt unverändert.
 *
 * Fasst weder den Formularzustand an noch entscheidet sie über den Aschegehalt beim Wechsel —
 * diese Regel liegt ausschließlich in `applyCategoryChange` (`alternativeFuelRows.ts`) und wird
 * von der Sektion (Plan 34-12) angewandt. Bewusst nicht hier verdoppelt, damit die Regel nicht in
 * zwei Dateien steht.
 */

import { ActionIcon, Menu, Text } from '@mantine/core';
import { IconCheck, IconSwitchHorizontal } from '@tabler/icons-react';

import type { FuelAshCategory } from '../../../services/fuelAshCategoriesApi.js';

export interface AlternativeFuelCategoryMenuProps {
  /** Die zwölf Sekundär-Kategorien, in der vom Server gelieferten Reihenfolge — unverändert
   *  weitergereicht, keine eigene Sortierung hier. */
  categories: FuelAshCategory[];
  /** Die Kategorie, in deren Gruppe die Zeile aktuell steht — deren Eintrag ist gesperrt (D-19). */
  currentCategoryId: number;
  disabled?: boolean;
  onSelect: (categoryId: number) => void;
}

export function AlternativeFuelCategoryMenu({
  categories,
  currentCategoryId,
  disabled,
  onSelect,
}: AlternativeFuelCategoryMenuProps) {
  return (
    <Menu withinPortal position="bottom-end" shadow="md">
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          disabled={disabled}
          aria-label="Kategorie wechseln"
          title="Kategorie wechseln"
        >
          <IconSwitchHorizontal size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {categories.map((category) => {
          // D-19: die aktuelle Kategorie ist markiert und NICHT waehlbar -- ein Klick darauf
          // loest zwar ohnehin nichts aus (applyCategoryChange liefert fuer denselben Wert
          // `null`), aber der Eintrag soll gar nicht erst waehlbar aussehen.
          const isCurrent = category.id === currentCategoryId;
          return (
            <Menu.Item
              key={category.id}
              disabled={isCurrent}
              leftSection={isCurrent ? <IconCheck size={14} /> : null}
              onClick={() => onSelect(category.id)}
            >
              <Text size="sm">{category.name}</Text>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
